import raw from "./electronicsCustomers.json";
import { PersonaConfig, PersonaOrder, OrderStatus } from "../types";

// Customer master for the NuPlay Electronics demo — generated from the
// "Nuplay_Electronics - Demo - Final_Master" CSV (the source of truth). All
// amounts are USD. This replaces the live Shopify customer summary, whose
// store currency (INR) and rolled-up totals did not match the real data.
export interface ElectronicsOrder {
  orderId: string;
  product: string;
  brand: string;
  category: string;
  status: string;
  statusRaw: string;
  totalUsd: number;
  quantity: number;
  carrier: string;
  trackingNumber: string;
  eta: string;
  date: string;
}

export interface ElectronicsCustomer {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  loyaltyTier: string;
  lifetimeValueBand: string;
  preferredChannel: string;
  crmNotes: string;
  ordersCount: number;
  totalSpentUsd: number;
  orders: ElectronicsOrder[];
}

export const ELECTRONICS_CUSTOMERS = raw as ElectronicsCustomer[];
export const ELECTRONICS_PERSONA_ID_PREFIX = "elec_";

const GRADIENTS = [
  "from-blue-500 to-indigo-600", "from-rose-500 to-pink-600", "from-violet-500 to-purple-600",
  "from-emerald-500 to-teal-600", "from-amber-500 to-orange-600", "from-cyan-500 to-blue-600",
  "from-fuchsia-500 to-pink-600", "from-lime-500 to-green-600",
];

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/);
  const ini = ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
  return ini || (name[0] ?? "?").toUpperCase();
}

export function getElectronicsCustomer(id: string): ElectronicsCustomer | undefined {
  return ELECTRONICS_CUSTOMERS.find((c) => c.id === id);
}

export function electronicsCustomerToPersona(c: ElectronicsCustomer): PersonaConfig {
  const idx = ELECTRONICS_CUSTOMERS.findIndex((x) => x.id === c.id);
  return {
    id: `${ELECTRONICS_PERSONA_ID_PREFIX}${c.id}`,
    name: c.name,
    role: c.crmNotes || "Customer",
    avatarInitials: initialsFor(c.name),
    avatarBg: GRADIENTS[(idx >= 0 ? idx : 0) % GRADIENTS.length],
    tagLine: c.crmNotes,
    email: c.email,
    phone: c.phone,
    accountId: c.id,
    memberSince: "",
    loyaltyTier: c.loyaltyTier,
    orders: c.orders.map((o): PersonaOrder => ({
      orderId: o.orderId,
      product: o.product,
      status: o.status as OrderStatus,
      carrier: o.carrier || undefined,
      trackingNumber: o.trackingNumber || undefined,
      eta: o.eta || undefined,
      date: o.date,
      totalUsd: o.totalUsd,
    })),
    ordersCount: c.ordersCount,
    bestFor: [],
    suggestedQuestions: {},
    city: c.city,
    state: c.state,
    totalSpentUsd: c.totalSpentUsd,
    lifetimeValueBand: c.lifetimeValueBand,
    preferredChannel: c.preferredChannel,
    crmNotes: c.crmNotes,
  };
}
